/**
 * DrillBreadcrumbs — clickable breadcrumb trail back to the top-level
 * statement. Rendered above the workspace canvas whenever the VQE store's
 * `drillStack` is non-empty (the user has focused into a subquery).
 *
 * Each breadcrumb truncates to the entry's `label` (the parent surface name +
 * a snippet of the subquery body). Clicking the root entry empties the stack;
 * clicking an intermediate entry pops back to that depth.
 */
import type { DrillEntry } from '../../types';

export interface DrillBreadcrumbsProps {
  readonly stack: readonly DrillEntry[];
  readonly onJumpTo: (depth: number) => void;
}

export function DrillBreadcrumbs({ stack, onJumpTo }: DrillBreadcrumbsProps) {
  if (stack.length === 0) return null;
  return (
    <nav
      className="flex items-center gap-1.5 border-b border-border bg-muted/30 px-4 py-1.5 text-xs"
      data-testid="visual-query-breadcrumbs"
      aria-label="Subquery breadcrumb trail"
    >
      <button
        type="button"
        onClick={() => onJumpTo(0)}
        className="rounded px-1.5 py-0.5 font-medium text-foreground hover:bg-muted"
        data-testid="visual-query-breadcrumb-root"
      >
        Top-level
      </button>
      {stack.map((entry, idx) => {
        const isLast = idx === stack.length - 1;
        return (
          <span key={`${entry.bodyStart}-${entry.bodyEnd}-${idx}`} className="flex items-center gap-1.5">
            <span aria-hidden className="text-muted-foreground/70">›</span>
            <button
              type="button"
              onClick={() => onJumpTo(idx + 1)}
              disabled={isLast}
              aria-current={isLast ? 'page' : undefined}
              className="rounded px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:text-foreground disabled:cursor-default disabled:text-foreground"
              data-testid={`visual-query-breadcrumb-${idx}`}
            >
              {entry.label}
            </button>
          </span>
        );
      })}
    </nav>
  );
}
